import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { OneEmptyState } from '@factorialco/f0-react/dist/experimental'
import { AtsJobApplicationFilesOfferLetterDocument } from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'

import { SendOfferLetterModal } from 'modules/ats/components/SendOfferLetterModal'
import { OfferLetterFromScratchModal } from 'modules/ats/components/SendOfferLetterModal/OfferLetterFromScratch'
import { OfferLetterFromTemplateModal } from 'modules/ats/components/SendOfferLetterModal/OfferLetterFromTemplate'
import { SenfOfferLetterFromTemplateModal } from 'modules/ats/components/SendOfferLetterModal/SenfOfferLetterFromTemplate'

import { OfferLetterItem } from './OfferLetterItem'

interface Props {
  applicationId: number
  jobPostingId: number
}

export const OfferLetter = ({ applicationId, jobPostingId }: Props) => {
  const { company } = useCurrentSession()
  const { navigation } = useNavigation()
  const navigate = useNavigate()
  const [openedModal, setOpenedModal] = useState<
    | 'send-offer-letter'
    | 'create-offer-letter-from-template'
    | 'send-offer-letter-from-scratch'
    | 'send-offer-letter-from-template'
    | undefined
  >(undefined)
  const { data } = useQuery(AtsJobApplicationFilesOfferLetterDocument, {
    variables: { applicationId, companyId: company.id, jobPostingId },
  })

  const devOfferLetterTemplatesEnabled =
    data?.features.enabledFeatures.nodes.find(
      (node) => node.name === 'dev_ats_offer_letter_templates'
    )?.enabled ?? false

  const offerLetterData = data?.ats.offerLetters[0]
  const applicationData = data?.ats.application

  if (!applicationData) {
    return null
  }

  return (
    <>
      {offerLetterData ? (
        <OfferLetterItem
          offerLetter={offerLetterData}
          applicationId={applicationId}
          jobPostingId={jobPostingId}
        />
      ) : (
        <OneEmptyState
          title={i18n.t('ats.candidates.files.offer_letter.empty_state.title')}
          description={i18n.t('ats.candidates.files.offer_letter.empty_state.description')}
          emoji="📝"
          actions={[
            {
              label: i18n.t(
                'ats.candidates.files.offer_letter.empty_state.actions.send_offer_letter'
              ),
              onClick: () => setOpenedModal('send-offer-letter'),
            },
            {
              label: i18n.t('ats.candidates.files.offer_letter.empty_state.actions.templates'),
              onClick: () => navigate(navigation.settings.ats.templates.fullPath()),
              variant: 'outline',
            },
          ]}
        />
      )}

      {openedModal === 'send-offer-letter' &&
        // TO DO: show only SendOfferLetterModal when feature is ready
        (devOfferLetterTemplatesEnabled ? (
          <SendOfferLetterModal
            onSelectOption={(option) => {
              setOpenedModal(option)
            }}
            onClose={() => setOpenedModal(undefined)}
          />
        ) : (
          <OfferLetterFromScratchModal
            application={applicationData}
            onClose={() => setOpenedModal(undefined)}
          />
        ))}

      {openedModal === 'send-offer-letter-from-scratch' && (
        <OfferLetterFromScratchModal
          application={applicationData}
          onClose={() => setOpenedModal(undefined)}
        />
      )}

      {openedModal === 'create-offer-letter-from-template' && (
        <OfferLetterFromTemplateModal
          jobApplicationId={applicationId}
          onClose={() => setOpenedModal(undefined)}
          onSuccess={() => setOpenedModal('send-offer-letter-from-template')}
        />
      )}

      {openedModal === 'send-offer-letter-from-template' && (
        <SenfOfferLetterFromTemplateModal
          applicationData={applicationData}
          onClose={() => setOpenedModal(undefined)}
        />
      )}
    </>
  )
}
